import { Directive, ElementRef, OnInit, ViewChild } from '@angular/core';

import { IInputMaskConfig } from '../../../../../models/input-mask-config.model';
import { InputType } from '../models/input-type.models';
import { MASK_CONSTANTS } from '../constants/mask.constants';
import { OfficeFieldWebInputTypeEnum } from '@pages/office/models/office-data.model';
import { BaseFormControlDirective } from '@pages/office/office-form/office-form/form-controls/base-form-control.directive';
import { isEmptyValue } from '@core/helpers/value-empty-checker.helper';

@Directive()
export class MaskedInputDirective extends BaseFormControlDirective implements OnInit {
  @ViewChild('inputRef', { static: false, read: ElementRef }) inputRef: ElementRef<HTMLInputElement>;

  public type: InputType;
  public inputMaskConfig: IInputMaskConfig;
  private decimalMarker: string;

  ngOnInit(): void {
    super.ngOnInit();
    this.inputMaskConfig = this.officeField.InputMaskConfig;
    this.decimalMarker = this.inputMaskConfig?.decimalMarker || '.';
  }

  public inputTransformFn = (value: string | number): string => {
    let result = value.toString();
    if (this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      const newValue = this.enforceMaxDigitsBeforeDecimal(result);
      result = result === newValue ? value.toString() : newValue;

      result = this.enforceMaxDigitsAfterDecimal(result);
    }
    if (value && this.officeField?.InputMask?.includes(MASK_CONSTANTS.upperCaseMask)) {
      result = result.toUpperCase();
    }
    return result;
  };

  private isNumericMask(mask: string): boolean {
    return mask?.trim()?.startsWith('#') || mask?.trim()?.startsWith('0');
  }

  private isIntegerWebInputType(): boolean {
    const type = this.officeField.WebInputType;
    return (
      type === OfficeFieldWebInputTypeEnum.byte ||
      type === OfficeFieldWebInputTypeEnum.int ||
      type === OfficeFieldWebInputTypeEnum.long ||
      type === OfficeFieldWebInputTypeEnum.short
    );
  }

  private shouldCalculateMaxDigitsBeforeAndAfterDecimal(): boolean {
    return this.officeField?.InputMask && !this.isIntegerWebInputType() && this.isNumericMask(this.officeField?.InputMask);
  }

  private countDigits(input: string | undefined): number {
    return input?.match(/[#0]/g)?.length ?? 0;
  }

  // Calculate from the original Vista mask number of digits before and after decimal point
  private calcMaxDigitsBeforeAndAfterDecimal(): [number, number] {
    if (this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      // Extract the part before and after the decimal marker from the Vista mask
      const [integerPart, decimalPart] = this.officeField.InputMask.split(this.decimalMarker);

      // Count all digit placeholders (# and 0) in the integer and decimal part
      return [this.countDigits(integerPart), this.countDigits(decimalPart)];
    }
  }

  private replaceSeparators(value: string, separators: string[], replacement = ''): string {
    if (!value || !separators?.length) {
      return value;
    }

    // Escape special characters and create a regex pattern
    const escapedSeparators = separators.map(sep => `\\${sep}`).join('');
    const regex = new RegExp(`[${escapedSeparators}]`, 'g');

    return value.replace(regex, replacement);
  }

  private enforceMaxDigitsBeforeDecimal(value: string): string {
    if (value?.length && this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      const [integerPart, decimalPart] = this.replaceSeparators(
        value,
        this.officeField.InputMaskConfig?.specialCharacters?.filter(c => c !== this.decimalMarker)
      ).split(this.decimalMarker);

      const [maxDigitsBeforeDecimal, maxDigitsAfterDecimal] = this.calcMaxDigitsBeforeAndAfterDecimal();
      if (integerPart.length > maxDigitsBeforeDecimal) {
        // Enforce max digits before decimal
        const limitedIntegerPart = integerPart.slice(0, maxDigitsBeforeDecimal);
        const extraIntegerPart = integerPart.slice(maxDigitsBeforeDecimal);

        let newDecimalPart = !!decimalPart || '';
        if (extraIntegerPart && this.inputMaskConfig?.decimalMarker) {
          newDecimalPart = (extraIntegerPart + newDecimalPart).slice(0, maxDigitsAfterDecimal);
        }
        // Reconstruct the value with the decimal part untouched
        const newValue = newDecimalPart ? `${limitedIntegerPart}${this.decimalMarker}${newDecimalPart}` : limitedIntegerPart;

        // Update the input value
        return newValue;
      }
    }
    return value;
  }

  private enforceMaxDigitsAfterDecimal(value: string): string {
    if (!value) {
      return value;
    }
    const decimalMarker = this.inputMaskConfig?.decimalMarker || '.';
    const sanitized = this.officeField?.InputMaskConfig?.specialCharacters
      ? this.replaceSeparators(
          value,
          this.officeField.InputMaskConfig.specialCharacters.filter(c => c !== decimalMarker)
        )
      : value;
    const parts = sanitized.split(decimalMarker);
    if (parts.length < 2) {
      return value; // No decimal part present
    }
    const [integerPart, decimalPart] = parts;
    if (!decimalPart?.length) {
      return value;
    }

    let maskMaxAfterDecimal: number | undefined;
    if (this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      const [, m] = this.calcMaxDigitsBeforeAndAfterDecimal() || [];
      maskMaxAfterDecimal = m;
    }
    if (isEmptyValue(maskMaxAfterDecimal) || decimalPart.length <= maskMaxAfterDecimal) {
      return value;
    }
    const truncatedDecimal = decimalPart.slice(0, maskMaxAfterDecimal);
    return `${integerPart}${decimalMarker}${truncatedDecimal}`;
  }
}
